import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import styled from "styled-components";
import { theme } from "styles/theme";
import { useQueryClient } from "@tanstack/react-query";
import { useAdminCreateDealerDeviceLink } from "hooks/useDealers";
import { notify } from "utils/notify";
import { FormField } from "components/shared/FormField";
import { FormLabel } from "components/shared/FormLabel";
import { FormError, getFieldAriaProps, fieldErrorId } from "components/shared/FormError";

const HelpText = styled.p`
  font-size: 0.9rem;
  color: ${theme.colors.textSecondary};
  margin-top: 0.25rem;
`;

const schema = yup.object({
  serialNumber: yup
    .string()
    .trim()
    .required("Device serial number is required"),
});

type FormData = yup.InferType<typeof schema>;

interface AddDeviceLinkDialogProps {
  visible: boolean;
  onHide: () => void;
  dealerId: number;
  dealerName: string;
}

const AddDeviceLinkDialog: React.FC<AddDeviceLinkDialogProps> = ({
  visible,
  onHide,
  dealerId,
  dealerName,
}) => {
  const queryClient = useQueryClient();
  const createDeviceLink = useAdminCreateDealerDeviceLink();
  const isPending = createDeviceLink.isPending;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: { serialNumber: "" },
  });

  const handleCancel = () => {
    reset({ serialNumber: "" });
    onHide();
  };

  const onSubmit = (data: FormData) => {
    const serialNumber = data.serialNumber.trim();

    createDeviceLink.mutate(
      { dealerId, serialNumber },
      {
        onSuccess: (result) => {
          notify.success(
            `Device ${serialNumber} has been linked to ${dealerName}`
          );
          if (result?.cancelledTemporaryAccessRequestIds?.length) {
            notify.info(
              `${dealerName}'s pending temporary access request for this device was cancelled.`
            );
          }
          reset({ serialNumber: "" });
          queryClient.invalidateQueries({
            queryKey: ["dealerInfo", dealerId.toString()],
          });
          onHide();
        },
        onError: (error: Error) => {
          notify.error(error.message || "Failed to link device");
        },
      }
    );
  };

  const footerContent = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={handleCancel}
        className="p-button-text"
        disabled={isPending}
      />
      <Button
        label="Link Device"
        icon="pi pi-link"
        onClick={handleSubmit(onSubmit)}
        loading={isPending}
        autoFocus
      />
    </div>
  );

  return (
    <Dialog
      header={`Link New Device to ${dealerName}`}
      visible={visible}
      style={{ width: "500px" }}
      onHide={handleCancel}
      footer={footerContent}
      closable={!isPending}
      closeOnEscape={!isPending}
    >
      <form className="p-fluid" onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormField>
          <FormLabel htmlFor="serialNumber">Device Serial Number</FormLabel>
          <InputText
            type="number"
            max={8}
            placeholder="Enter device serial number"
            {...register("serialNumber")}
            {...getFieldAriaProps("serialNumber", errors.serialNumber?.message)}
            className={errors.serialNumber ? "p-invalid" : ""}
            disabled={isPending}
          />
          <FormError id={fieldErrorId("serialNumber")}>
            {errors.serialNumber?.message}
          </FormError>
          <HelpText>
            Enter the serial number of the device you want to link to this
            dealer. The device must already be registered in the system.
          </HelpText>
        </FormField>
      </form>
    </Dialog>
  );
};

export default AddDeviceLinkDialog;
